import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { handleApiErrors } from "@/utils/errorMapper";
import { useMentorApi } from "@/api/MentorApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMentorStore } from "@/stores/mentorStore";

interface AddMentorProps {
  onSuccess?: () => void;
}

interface MentorFormInputs {
  nom: string;
  prenom: string;
  profession: string;
}

const AddMentor: React.FC<AddMentorProps> = ({ onSuccess }) => {
  const { createMentor } = useMentorApi();
  const { closeModal, modal } = useMentorStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<MentorFormInputs>();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: MentorFormInputs) => {
    setIsSubmitting(true);
    const lastConnected = new Date().toISOString();

    const result = await createMentor(
      data.nom,
      data.prenom,
      data.profession,
      lastConnected,
    );

    setIsSubmitting(false);

    if (result && result.success) {
      if (onSuccess) onSuccess();
      reset();
      setImagePreview("");
      closeModal();
    } else if (result && result.error) {
      handleApiErrors(result.error, setError);
    }
  };

  return (
    <Dialog open={modal.isOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-md p-6 bg-white rounded-2xl shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-800 mb-2">
            Créer un Mentor
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Avatar + Upload Button */}
          <div className="flex flex-col items-center gap-3">
            <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-gray-200 shadow-sm">
              <img
                src={imagePreview || "/placeholder-avatar.png"}
                alt="Profile preview"
                className="h-full w-full object-cover"
              />
            </div>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="text-sm px-4"
            >
              Choisir une image
            </Button>
          </div>

          {/* Input fields */}
          <div className="space-y-4">
            <div className="space-y-1">
              <input
                type="text"
                placeholder="Nom"
                {...register("nom", { required: "Le nom est requis" })}
                className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {errors.nom && (
                <p className="text-red-500 text-xs px-1">
                  {errors.nom.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <input
                type="text"
                placeholder="Prénom"
                {...register("prenom", { required: "Le prénom est requis" })}
                className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {errors.prenom && (
                <p className="text-red-500 text-xs px-1">
                  {errors.prenom.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <input
                type="text"
                placeholder="Profession"
                {...register("profession", {
                  required: "La profession est requise",
                })}
                className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {errors.profession && (
                <p className="text-red-500 text-xs px-1">
                  {errors.profession.message}
                </p>
              )}
            </div>
          </div>

          {/* Submit button */}
          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Création en cours..." : "Créer Mentor"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMentor;
